import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, TrendingUp, DollarSign, Clock, Search, Filter, 
  Download, RefreshCw, Eye, Edit2, Trash2, Plus, X, 
  CheckCircle, Phone, Mail, Calendar, MessageSquare,
  BarChart3, PieChart, ArrowUpRight, ArrowDownRight,
  LogOut, Settings, Bell, User, ChevronDown, MoreVertical,
  AlertCircle, Star, Tag
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status colors and labels
const statusConfig = {
  new: { color: 'bg-blue-500', label: 'جديد', labelEn: 'New' },
  contacted: { color: 'bg-yellow-500', label: 'تم التواصل', labelEn: 'Contacted' },
  in_progress: { color: 'bg-purple-500', label: 'قيد المعالجة', labelEn: 'In Progress' },
  qualified: { color: 'bg-cyan-500', label: 'مؤهل', labelEn: 'Qualified' },
  converted: { color: 'bg-green-500', label: 'تم التحويل', labelEn: 'Converted' },
  closed: { color: 'bg-gray-500', label: 'مغلق', labelEn: 'Closed' }
};

const priorityConfig = {
  low: { color: 'bg-gray-400', label: 'منخفضة', labelEn: 'Low' },
  medium: { color: 'bg-yellow-400', label: 'متوسطة', labelEn: 'Medium' },
  high: { color: 'bg-orange-500', label: 'عالية', labelEn: 'High' },
  urgent: { color: 'bg-red-500', label: 'عاجل', labelEn: 'Urgent' }
};

// Login Component
const LoginPage = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/admin/verify`, {
        headers: {
          'Authorization': 'Basic ' + btoa(`${username}:${password}`)
        }
      });

      if (response.ok) {
        localStorage.setItem('adminAuth', btoa(`${username}:${password}`));
        onLogin();
      } else {
        setError('اسم المستخدم أو كلمة المرور غير صحيحة');
      }
    } catch (err) {
      setError('خطأ في الاتصال بالخادم');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A1628] via-[#0f2444] to-[#0A1628] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#C9A55A] to-[#D4AF37] rounded-2xl flex items-center justify-center mb-4 shadow-[0_0_30px_rgba(201,165,90,0.3)]">
            <Settings className="w-10 h-10 text-[#0A1628]" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">لوحة التحكم</h1>
          <p className="text-gray-400">مكتب قاسم حسين للمحاماة</p>
        </div>

        {/* Login Form */}
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2 text-right">
                اسم المستخدم
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-right focus:outline-none focus:border-[#C9A55A]/50"
                placeholder="admin"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2 text-right">
                كلمة المرور
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-right focus:outline-none focus:border-[#C9A55A]/50"
                placeholder="••••••••"
                required
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 p-3 rounded-lg">
                <AlertCircle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-[#C9A55A] to-[#D4AF37] text-[#0A1628] rounded-xl font-bold text-lg hover:shadow-[0_0_30px_rgba(201,165,90,0.4)] transition-all disabled:opacity-50"
            >
              {loading ? 'جاري الدخول...' : 'تسجيل الدخول'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, change, changeType, color }) => (
  <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-[#C9A55A]/30 transition-all">
    <div className="flex items-start justify-between">
      <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      {change && (
        <div className={`flex items-center gap-1 text-sm ${changeType === 'up' ? 'text-green-400' : 'text-red-400'}`}>
          {changeType === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
          <span>{change}</span>
        </div>
      )}
    </div>
    <p className="text-3xl font-bold text-white mt-4">{value}</p>
    <p className="text-gray-400 text-sm mt-1">{label}</p>
  </div>
);

// Lead Row Component
const LeadRow = ({ lead, onView, onEdit, onDelete }) => {
  const status = statusConfig[lead.status] || statusConfig.new;
  const priority = priorityConfig[lead.priority] || priorityConfig.medium;
  
  return (
    <tr className="border-b border-white/5 hover:bg-white/5 transition-colors">
      <td className="py-4 px-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#C9A55A]/20 to-[#C9A55A]/10 rounded-full flex items-center justify-center text-[#C9A55A] font-bold">
            {lead.name.charAt(0)}
          </div>
          <div className="text-right">
            <p className="font-medium text-white">{lead.name}</p>
            <p className="text-sm text-gray-400">{lead.email}</p>
          </div>
        </div>
      </td>
      <td className="py-4 px-4 text-gray-300">{lead.phone}</td>
      <td className="py-4 px-4 text-[#C9A55A] font-bold">{lead.amount}</td>
      <td className="py-4 px-4">
        <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${status.color}`}>
          {status.label}
        </span>
      </td>
      <td className="py-4 px-4">
        <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${priority.color}`}>
          {priority.label}
        </span>
      </td>
      <td className="py-4 px-4 text-gray-400 text-sm">
        {new Date(lead.created_at).toLocaleDateString('ar-SA')}
      </td>
      <td className="py-4 px-4">
        <div className="flex items-center gap-2">
          <button onClick={() => onView(lead)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="عرض">
            <Eye className="w-4 h-4 text-gray-400 hover:text-white" />
          </button>
          <button onClick={() => onEdit(lead)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="تعديل">
            <Edit2 className="w-4 h-4 text-gray-400 hover:text-[#C9A55A]" />
          </button>
          <button onClick={() => onDelete(lead)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="حذف">
            <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-400" />
          </button>
        </div>
      </td>
    </tr>
  );
};

// Lead Detail Modal
const LeadDetailModal = ({ lead, onClose, onSave, authHeader }) => {
  const [editedLead, setEditedLead] = useState(lead);
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/admin/leads/${lead.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify({
          status: editedLead.status,
          priority: editedLead.priority
        })
      });
      onSave();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await fetch(`${API_URL}/api/admin/leads/${lead.id}/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify({ note: newNote })
      });
      setNewNote('');
      onSave();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-[#0A1628] via-[#0f2444] to-[#0A1628] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-[#C9A55A]/20">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X className="w-5 h-5 text-gray-400" />
          </button>
          <h2 className="text-xl font-bold text-white">تفاصيل العميل</h2>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Client Info */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-white/5 rounded-xl p-4 text-right">
              <div className="flex items-center gap-2 justify-end text-gray-400 mb-2">
                <span className="text-sm">الاسم</span>
                <User className="w-4 h-4" />
              </div>
              <p className="text-white font-medium">{lead.name}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 text-right">
              <div className="flex items-center gap-2 justify-end text-gray-400 mb-2">
                <span className="text-sm">الهاتف</span>
                <Phone className="w-4 h-4" />
              </div>
              <p className="text-white font-medium" dir="ltr">{lead.phone}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 text-right">
              <div className="flex items-center gap-2 justify-end text-gray-400 mb-2">
                <span className="text-sm">البريد الإلكتروني</span>
                <Mail className="w-4 h-4" />
              </div>
              <p className="text-white font-medium text-sm">{lead.email}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 text-right">
              <div className="flex items-center gap-2 justify-end text-gray-400 mb-2">
                <span className="text-sm">المبلغ</span>
                <DollarSign className="w-4 h-4" />
              </div>
              <p className="text-[#C9A55A] font-bold text-lg">{lead.amount}</p>
            </div>
          </div>

          {/* Status & Priority */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="text-right">
              <label className="block text-sm text-gray-400 mb-2">الحالة</label>
              <select
                value={editedLead.status}
                onChange={(e) => setEditedLead({ ...editedLead, status: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-right focus:outline-none focus:border-[#C9A55A]/50"
              >
                {Object.entries(statusConfig).map(([key, { label }]) => (
                  <option key={key} value={key} className="bg-[#0A1628]">{label}</option>
                ))}
              </select>
            </div>
            <div className="text-right">
              <label className="block text-sm text-gray-400 mb-2">الأولوية</label>
              <select
                value={editedLead.priority}
                onChange={(e) => setEditedLead({ ...editedLead, priority: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-right focus:outline-none focus:border-[#C9A55A]/50"
              >
                {Object.entries(priorityConfig).map(([key, { label }]) => (
                  <option key={key} value={key} className="bg-[#0A1628]">{label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Notes */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={handleAddNote}
                disabled={!newNote.trim()}
                className="px-3 py-1.5 bg-[#C9A55A] text-[#0A1628] rounded-lg text-sm font-medium disabled:opacity-50"
              >
                إضافة
              </button>
              <h3 className="text-white font-medium flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                الملاحظات
              </h3>
            </div>
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="أضف ملاحظة جديدة..."
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-right focus:outline-none focus:border-[#C9A55A]/50 mb-3"
              rows={2}
            />
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {lead.notes && lead.notes.length > 0 ? (
                lead.notes.map((note, idx) => (
                  <div key={idx} className="bg-white/5 rounded-lg p-3 text-right">
                    <p className="text-gray-300 text-sm">{note}</p>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-sm text-center py-4">لا توجد ملاحظات</p>
              )}
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4 text-sm text-gray-400">
            <div className="flex items-center gap-2 justify-end">
              <span>{new Date(lead.created_at).toLocaleString('ar-SA')}</span>
              <span>تاريخ التسجيل:</span>
            </div>
            <div className="flex items-center gap-2 justify-end">
              <span>{new Date(lead.updated_at).toLocaleString('ar-SA')}</span>
              <span>آخر تحديث:</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-6 py-2.5 border border-white/20 text-gray-300 rounded-xl hover:bg-white/5"
          >
            إلغاء
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2.5 bg-gradient-to-r from-[#C9A55A] to-[#D4AF37] text-[#0A1628] rounded-xl font-medium disabled:opacity-50"
          >
            {saving ? 'جاري الحفظ...' : 'حفظ التغييرات'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Admin Dashboard
const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  const authHeader = `Basic ${localStorage.getItem('adminAuth') || ''}`;

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const auth = localStorage.getItem('adminAuth');
      if (!auth) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/admin/verify`, {
          headers: { 'Authorization': `Basic ${auth}` }
        });
        if (response.ok) {
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('adminAuth');
        }
      } catch (err) {
        localStorage.removeItem('adminAuth');
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const [statsRes, leadsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/dashboard`, { headers: { 'Authorization': authHeader } }),
        fetch(`${API_URL}/api/admin/leads?status=${statusFilter}&priority=${priorityFilter}&search=${searchQuery}`, { 
          headers: { 'Authorization': authHeader } 
        })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (leadsRes.ok) setLeads(await leadsRes.json());
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  }, [isAuthenticated, authHeader, statusFilter, priorityFilter, searchQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['الاسم', 'الهاتف', 'البريد', 'المبلغ', 'الحالة', 'الأولوية', 'تاريخ التسجيل'];
    const rows = leads.map(l => [
      l.name,
      l.phone,
      l.email,
      l.amount,
      statusConfig[l.status]?.label || l.status,
      priorityConfig[l.priority]?.label || l.priority,
      new Date(l.created_at).toLocaleDateString('ar-SA')
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `leads_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    setIsAuthenticated(false);
  };

  const handleDelete = async (lead) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا العميل؟')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/leads/${lead.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': authHeader }
      });
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A1628] via-[#0f2444] to-[#0A1628] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#C9A55A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage onLogin={() => setIsAuthenticated(true)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A1628] via-[#0f2444] to-[#0A1628]" dir="rtl">
      {/* Header */}
      <header className="bg-white/5 backdrop-blur-xl border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={handleLogout} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
                <LogOut className="w-5 h-5" />
                <span>تسجيل الخروج</span>
              </button>
            </div>
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold text-white">لوحة التحكم - CRM</h1>
              <div className="w-10 h-10 bg-gradient-to-br from-[#C9A55A] to-[#D4AF37] rounded-xl flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-[#0A1628]" />
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              icon={Users}
              label="إجمالي العملاء"
              value={stats.total_leads}
              color="bg-blue-500"
            />
            <StatCard
              icon={Star}
              label="عملاء جدد"
              value={stats.new_leads}
              change={`${stats.leads_today} اليوم`}
              changeType="up"
              color="bg-green-500"
            />
            <StatCard
              icon={Clock}
              label="قيد المعالجة"
              value={stats.in_progress_leads}
              color="bg-purple-500"
            />
            <StatCard
              icon={DollarSign}
              label="إجمالي المبالغ"
              value={stats.total_amount}
              color="bg-[#C9A55A]"
            />
          </div>
        )}

        {/* Secondary Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-4 border border-white/10">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-white">{stats.leads_today}</span>
                <span className="text-gray-400 text-sm">عملاء اليوم</span>
              </div>
            </div>
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-4 border border-white/10">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-white">{stats.leads_this_week}</span>
                <span className="text-gray-400 text-sm">هذا الأسبوع</span>
              </div>
            </div>
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-4 border border-white/10">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-white">{stats.leads_this_month}</span>
                <span className="text-gray-400 text-sm">هذا الشهر</span>
              </div>
            </div>
          </div>
        )}

        {/* Leads Table */}
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden">
          {/* Table Header */}
          <div className="p-6 border-b border-white/10">
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
              <div className="flex items-center gap-3 w-full lg:w-auto">
                <button
                  onClick={exportToCSV}
                  className="flex items-center gap-2 px-4 py-2.5 bg-green-500/20 text-green-400 rounded-xl hover:bg-green-500/30 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>تصدير</span>
                </button>
                <button
                  onClick={fetchData}
                  className="flex items-center gap-2 px-4 py-2.5 bg-white/10 text-gray-300 rounded-xl hover:bg-white/20 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>تحديث</span>
                </button>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="بحث..."
                    className="w-full sm:w-48 pl-4 pr-10 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-right focus:outline-none focus:border-[#C9A55A]/50"
                  />
                </div>
                
                {/* Status Filter */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#C9A55A]/50"
                >
                  <option value="" className="bg-[#0A1628]">جميع الحالات</option>
                  {Object.entries(statusConfig).map(([key, { label }]) => (
                    <option key={key} value={key} className="bg-[#0A1628]">{label}</option>
                  ))}
                </select>
                
                {/* Priority Filter */}
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#C9A55A]/50"
                >
                  <option value="" className="bg-[#0A1628]">جميع الأولويات</option>
                  {Object.entries(priorityConfig).map(([key, { label }]) => (
                    <option key={key} value={key} className="bg-[#0A1628]">{label}</option>
                  ))}
                </select>
              </div>
              
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-[#C9A55A]" />
                قائمة العملاء
              </h2>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 bg-white/5">
                  <th className="text-right py-4 px-4 text-sm font-medium text-gray-400">العميل</th>
                  <th className="text-right py-4 px-4 text-sm font-medium text-gray-400">الهاتف</th>
                  <th className="text-right py-4 px-4 text-sm font-medium text-gray-400">المبلغ</th>
                  <th className="text-right py-4 px-4 text-sm font-medium text-gray-400">الحالة</th>
                  <th className="text-right py-4 px-4 text-sm font-medium text-gray-400">الأولوية</th>
                  <th className="text-right py-4 px-4 text-sm font-medium text-gray-400">التاريخ</th>
                  <th className="text-right py-4 px-4 text-sm font-medium text-gray-400">الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {leads.length > 0 ? (
                  leads.map(lead => (
                    <LeadRow
                      key={lead.id}
                      lead={lead}
                      onView={setSelectedLead}
                      onEdit={setSelectedLead}
                      onDelete={handleDelete}
                    />
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-gray-400">
                      <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>لا يوجد عملاء حتى الآن</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Lead Detail Modal */}
      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onSave={() => {
            fetchData();
            setSelectedLead(null);
          }}
          authHeader={authHeader}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
